'use client'

import {
  darkTheme,
  RainbowKitProvider,
} from '@rainbow-me/rainbowkit'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import {
  PropsWithChildren,
  useEffect,
  useState,
} from 'react'
import { Provider as ReactReduxProvider } from 'react-redux'
import { WagmiProvider } from 'wagmi'

import '@rainbow-me/rainbowkit/styles.css'
import { ConfirmDialogProvider } from '@/components/confirm-dialog'
import { EnvProvider } from '@/components/env-provider'
import { InfoDialogProvider } from '@/components/info-dialog'
import Init from '@/components/init'
import ToastProvider from '@/components/toast-provider'
import { ACTIVE_CAIP_NETWORK_ID } from '@/constants/local-storage'
import {
  Env,
  getEnv,
} from '@/lib/env'
import { sleep } from '@/lib/utils'
import { getWagmiConfig } from '@/lib/wagmi-config'
import { store } from '@/redux'
import '@/lib/web-socket'

const queryClient = new QueryClient()
const rainbowKitTheme = darkTheme({
  accentColor: '#486cf7',
})

export interface ClientProviderProps extends PropsWithChildren {
  host: string
}

export default function ClientProvider({ children, host }: ClientProviderProps) {
  const [ready, setReady] = useState(false)
  const env = getEnv(host)
  useEffect(() => {
    const cachedNetworkId = window.localStorage.getItem(ACTIVE_CAIP_NETWORK_ID)
    if (cachedNetworkId === 'eip155:1') {
      window.localStorage.removeItem(ACTIVE_CAIP_NETWORK_ID)
    }
    const init = async () => {
      if (env !== Env.Development) {
        setReady(true)
        return
      }
      console.log(process.env.NEXT_PUBLIC_MOCK)
      if (process.env.NEXT_PUBLIC_MOCK === 'true') {
        const { setupWorker } = await import('msw/browser')
        const { http, delay } = await import('msw')
        const { botHandlers } = await import('@/mock/bot')
        const { betHandlers } = await import('@/mock/bet')
        const { competitionHandlers } = await import('@/mock/competition')
        const { notificationHandlers } = await import('@/mock/notification')
        const { userHandler } = await import('@/mock/user')

        const worker = setupWorker(
          http.all('*', async () => {
            await delay(1000)
          }),
          ...botHandlers,
          ...betHandlers,
          ...notificationHandlers,
          ...competitionHandlers,
          ...userHandler,
        )

        await worker.start()
        await sleep(1000)
      }
      setReady(true)
    }
    init()
  }, [env])

  if (!ready) return null

  return (
    <EnvProvider env={getEnv(host)}>
      <WagmiProvider config={getWagmiConfig(env)}>
        <QueryClientProvider client={queryClient}>
          <RainbowKitProvider theme={rainbowKitTheme}>
            <ReactReduxProvider store={store}>
              <ConfirmDialogProvider>
                <InfoDialogProvider>
                  <Init />
                  <ToastProvider />
                  {children}
                </InfoDialogProvider>
              </ConfirmDialogProvider>
            </ReactReduxProvider>
          </RainbowKitProvider>
        </QueryClientProvider>
      </WagmiProvider>
    </EnvProvider>
  )
}
